import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core"
import { Allocations, getSuperTypeName, isModelNode, NODE_SUPERTYPE } from "../../common/linqset-model";
import { CARBON_SCOPE, ModelInsights } from "../../common/organisation";
import { MODEL_COLOURS } from "../../model-services/model-item-enums";
import { BarData } from "../../charts/bar-chart/bar-chart.component";

@Component({
    selector: 'app-allocations-graph',
    templateUrl: './allocations-graph.html',
    styleUrls: ['./allocations-graph.css'],
    standalone: false
})
export class AllocationsGraphComponent implements OnInit, OnChanges {
    @Input() insights:ModelInsights
    @Input() comparisonInsights:ModelInsights
    @Input() comparisonName:string
    @Input() type:string
    @Input() height:number
    @Input() width:number
    
    barData:BarData[]
    stacks:string[]

    constructor() {}

    ngOnInit(): void {
        this.populateDataSource()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['insights'] || changes['comparisonInsights']) this.populateDataSource()
    }

    getSuperType(superType:NODE_SUPERTYPE) {
        return getSuperTypeName(superType)
    }

    isCost() {
        return this.type==="cost"
    }

    populateDataSource() {
        const allocations = this.isCost()?this.insights.costAllocations:this.insights.emissions
        const comparison = this.comparisonInsights && (this.isCost()?this.comparisonInsights.costAllocations:this.comparisonInsights.emissions)
        this.barData = [new BarData('allocated', "Allocated " + this.type, [], [], [], [], [], [])]
        this.stacks = []

        if (allocations && allocations.length > 0) {
            // org model so use allocations
            this.barData.push(new BarData('unallocated', "Unallocated " + this.type, [], [], [], [], [], []))
            this.barData.push(new BarData('unodelled', "Unmodelled " + this.type, [], [], [], [], [], []))           

            allocations.forEach(a => {
                this.stacks.push(a.superType?this.getSuperType(a.superType):a.scope)
                const ca = comparison && comparison.find(ca => ca.scope === a.scope && ca.superType === a.superType)
                this.setValues(a, ca)
            })
            if (comparison) {
                comparison.filter(ca => !allocations.find(a => a.scope === ca.scope && a.superType === ca.superType)).forEach(ca => {
                    this.stacks.push(ca.superType?this.getSuperType(ca.superType):ca.scope)
                    this.setValues(undefined, ca)
                })
            }
        }else {
            // normal model so use allocated costs/emissions
            if (this.isCost()) {
                if (this.insights.superTypes) {
                    Object.keys(this.insights.superTypes).map(t => {
                        const sType = parseInt(t) as NODE_SUPERTYPE
                        if (!isModelNode(sType)) {
                            const colours = this.getColours(undefined, sType)
                            this.stacks.push(this.getSuperType(sType))

                            this.barData[0].values.push(this.insights.superTypes[sType].allocatedCost)
                            this.barData[0].colors.push(colours[0])
                            this.barData[0].hoverColors.push(colours[1])
                            if (this.comparisonInsights) {
                                this.barData[0].csValues.push(this.comparisonInsights.superTypes[sType].allocatedCost)
                                this.barData[0].csColors.push(colours[0])    
                                this.barData[0].csHoverColors.push(colours[1])    
                            }
                        }
                    })
                }
            }else {
                Object.values(CARBON_SCOPE).forEach(s => {
                    const colours = this.getColours(s, undefined)
                    this.stacks.push(s.toString())

                    this.barData[0].values.push(this.insights.allocatedCarbonByScope?this.insights.allocatedCarbonByScope.map[s]:0)
                    this.barData[0].colors.push(colours[0])
                    this.barData[0].hoverColors.push(colours[1])
                    if (this.comparisonInsights) {
                        this.barData[0].csValues.push(this.comparisonInsights.allocatedCarbonByScope?this.comparisonInsights.allocatedCarbonByScope.map[s]:0)
                        this.barData[0].csColors.push(colours[0])    
                        this.barData[0].csHoverColors.push(colours[1])    
                    }

                })
            }
        }
    }

    setValues(allocation1?:Allocations, allocation2?:Allocations) {

        const colours = allocation1?this.getColours(allocation1.scope, allocation1.superType):this.getColours(allocation2.scope, allocation2.superType)
        this.barData[0].values.push(allocation1?.allocated||0)
        this.barData[0].colors.push(colours[0])
        this.barData[0].hoverColors.push(colours[1])
        this.barData[1].values.push(allocation1?.unallocated||0)
        this.barData[1].colors.push(colours[0])
        this.barData[1].hoverColors.push(colours[1])
        this.barData[2].values.push(allocation1?.unmodelled||0)
        this.barData[2].colors.push(colours[0])
        this.barData[2].hoverColors.push(colours[1])

        if (this.comparisonInsights) {
            this.barData[0].csValues.push(allocation2?.allocated||0)
            this.barData[0].csColors.push(colours[0])
            this.barData[0].csHoverColors.push(colours[1])
    
            this.barData[1].csValues.push(allocation2?.unallocated||0)
            this.barData[1].csColors.push(colours[0])
            this.barData[1].csHoverColors.push(colours[1])

            this.barData[2].csValues.push(allocation2?.unmodelled||0)
            this.barData[2].csColors.push(colours[0])
            this.barData[2].csHoverColors.push(colours[1])

        }

    }

    getColours(scope:CARBON_SCOPE, superType:NODE_SUPERTYPE) {
        if (scope) {
            switch (scope) {
                case CARBON_SCOPE.ScopeOne:
                    return [MODEL_COLOURS.CARBON_SCOPE1_COLOUR, MODEL_COLOURS.CARBON_SCOPE1_HOVER_COLOUR]
                case CARBON_SCOPE.ScopeTwo:
                    return [MODEL_COLOURS.CARBON_SCOPE2_COLOUR, MODEL_COLOURS.CARBON_SCOPE2_HOVER_COLOUR]
                case CARBON_SCOPE.ScopeThree:
                    return [MODEL_COLOURS.CARBON_SCOPE3_COLOUR, MODEL_COLOURS.CARBON_SCOPE3_HOVER_COLOUR]
            }
        }else {
            switch (superType) {
                case NODE_SUPERTYPE.INFRASTRUCTURE:
                    return [MODEL_COLOURS.INFRASTRUCTURE_COLOR, MODEL_COLOURS.INFRASTRUCTURE_HOVER_COLOR]
                case NODE_SUPERTYPE.PEOPLE:
                    return [MODEL_COLOURS.PEOPLE_COLOR, MODEL_COLOURS.PEOPLE_HOVER_COLOR]
                case NODE_SUPERTYPE.ROLE:
                    return [MODEL_COLOURS.ROLE_COLOR, MODEL_COLOURS.ROLE_HOVER_COLOR]
                case NODE_SUPERTYPE.SYSTEM:
                    return [MODEL_COLOURS.SYSTEM_COLOR, MODEL_COLOURS.SYSTEM_HOVER_COLOR]
            }
        }
        return undefined
    }
}
